import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { PiPlusBold, PiSpeakerHigh, PiStopFill, PiTrash } from "react-icons/pi";
import { useQueryClient } from "@tanstack/react-query";
import {
  SAVE_WORD_LIMIT_ERROR,
  StorySentence,
  fetchSentenceAudioUrl,
  useRemoveWordMutation,
  useSaveWordMutation,
  useWordExplainQuery,
} from "./queries";
import { invalidateStoryPracticeCount } from "./practiceApi";
import { useLoggedIn } from "./firebase";
import { lstr } from "./localization";
import { SkeletonBlock } from "./Skeleton";
import { ExplanationText } from "./explanationMarkup";

// Height the popup is assumed to reach once the explanation has loaded. The
// above/below placement is decided against this estimate instead of the live
// height: the popup is small while the explanation is loading, and deciding
// on the live height would flip the popup to the other side of the word the
// moment content arrives - moving the Listen button right before a click.
const ESTIMATED_LOADED_POPUP_HEIGHT_PX = 200;

// The popup starts this far left of its word, so the word's start stays in
// view beside the popup's edge; it is shifted further only to fit on screen.
const ANCHOR_NUDGE_PX = 16;
// Smallest gap kept between the popup and the edges of the screen.
const SCREEN_MARGIN_PX = 8;

// DOM id of the popup, for its word's aria-controls; at most one popup is
// open at a time.
export const WORD_EXPLANATION_POPUP_ID = "word-explanation-popup";
const POPUP_HEADING_ID = `${WORD_EXPLANATION_POPUP_ID}-heading`;

export function WordExplanationPopup({
  storyId,
  l,
  r,
  lSentenceIdx,
  rSentenceIdx,
  wordIdx,
  word,
  wordClassName,
}: {
  storyId: string;
  l: string;
  r: string;
  lSentenceIdx: number;
  rSentenceIdx: number;
  wordIdx: number;
  // The tapped word as it appears in the text, shown as the heading.
  word: string;
  // Its color in the text (noun gender), echoed in the heading.
  wordClassName: string;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [above, setAbove] = useState(false);
  const [shiftX, setShiftX] = useState(0);

  const query = useWordExplainQuery(
    storyId,
    l,
    r,
    lSentenceIdx,
    rSentenceIdx,
    wordIdx,
  );
  // Kept when a re-fetch fails, so the explanation stays on screen.
  const explanation = query.data;
  const storySentence = { storyId, r, rSentenceIdx };

  // Fetching the explanation records the lookup once the word's dictionary
  // entry is ready, which can add the word to the story's words to practice.
  const queryClient = useQueryClient();
  const lookupRecorded =
    query.isFetchedAfterMount &&
    explanation !== undefined &&
    explanation.dictionaryEntryId !== null &&
    !explanation.dictionaryEntryPreparing;
  useEffect(() => {
    if (lookupRecorded) invalidateStoryPracticeCount(queryClient, r, storyId);
  }, [lookupRecorded, queryClient, r, storyId]);

  const reposition = useCallback(() => {
    const popup = popupRef.current;
    if (!popup || !popup.offsetParent) return;

    const parentRect = (
      popup.offsetParent as HTMLElement
    ).getBoundingClientRect();
    const popupWidth = popup.offsetWidth;
    const popupHeight = popup.offsetHeight;

    const gap = 6;
    const spaceBelow = window.innerHeight - parentRect.bottom;
    // max() so a popup that turned out even taller than the estimate can
    // still flip above as a last resort instead of clipping off-screen.
    const effectiveHeight = Math.max(
      popupHeight,
      ESTIMATED_LOADED_POPUP_HEIGHT_PX,
    );
    setAbove(
      spaceBelow < effectiveHeight + gap &&
        parentRect.top > effectiveHeight + gap,
    );

    // clientWidth leaves out a desktop scrollbar, which innerWidth includes.
    const screenWidth = document.documentElement.clientWidth;
    const naturalLeft = parentRect.left - ANCHOR_NUDGE_PX;
    let shift = 0;
    if (naturalLeft + popupWidth > screenWidth - SCREEN_MARGIN_PX) {
      shift = screenWidth - SCREEN_MARGIN_PX - naturalLeft - popupWidth;
    }
    if (naturalLeft + shift < SCREEN_MARGIN_PX) {
      shift = SCREEN_MARGIN_PX - naturalLeft;
    }
    setShiftX(shift);
  }, []);

  useLayoutEffect(() => {
    reposition();
  });

  useEffect(() => {
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [reposition]);

  const strings = lstr(l);

  return (
    <div
      ref={popupRef}
      id={WORD_EXPLANATION_POPUP_ID}
      role="dialog"
      aria-labelledby={POPUP_HEADING_ID}
      // The explanation is in the spoken language, while the popup sits
      // inside the learned-language text.
      lang={l}
      // The popup sits inside the story text, so it resets the reading
      // typography to the UI's. It must read as clearly lifted off the page
      // in both themes: in light a heavy drop shadow does the separating
      // (white on cream is nearly identical); in dark, where shadows barely
      // show, the fill is lifted above the surface tone and the border is
      // brighter.
      className={`animate-pop-in absolute z-50 flex cursor-text select-text flex-col rounded-2xl border border-border bg-surface px-4 pb-4 pt-3.5 text-left font-sans text-[15px] font-normal leading-normal shadow-pop dark:border-white/15 dark:bg-[#2b2722] ${
        above ? "origin-bottom-left" : "origin-top-left"
      }`}
      style={{
        ...(above
          ? { bottom: "100%", marginBottom: 6 }
          : { top: "100%", marginTop: 6 }),
        // Shifted with left, not transform: the opening animation owns
        // transform, and its end state would override a shift there.
        left: shiftX - ANCHOR_NUDGE_PX,
        width: "min(24rem, calc(100vw - 1rem))",
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        id={POPUP_HEADING_ID}
        lang={r}
        className={`font-literata text-xl font-bold leading-tight ${
          wordClassName || "text-main-text"
        }`}
      >
        {word}
      </div>
      {/* The actions row (Listen + save) is pinned to the popup's anchored
          edge: under the heading when the popup opens below the word
          (top-anchored, grows downward), at the bottom when it opens above
          (bottom-anchored, grows upward). That way the buttons keep their
          position on the page when the loading placeholder is replaced by
          the explanation text. The edge is switched via CSS order, NOT by
          rendering the row in two conditional slots: the row must keep its
          React identity across an above/below flip, or the remount would
          stop in-progress audio playback. The save button mounts to the
          RIGHT of Listen once the explanation arrives, so Listen itself
          never moves. */}
      <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-2 ${
          above ? "order-last mt-3.5" : "mb-3 mt-2.5"
        }`}
      >
        <ListenSentenceButton
          l={l}
          storyId={storyId}
          r={r}
          rSentenceIdx={rSentenceIdx}
        />
        {explanation &&
          (explanation.dictionaryEntryId !== null ||
            explanation.dictionaryEntryPreparing) && (
            <SaveWordButton
              l={l}
              dictionaryEntryId={explanation.dictionaryEntryId}
              alreadySaved={explanation.alreadySaved}
              storySentence={storySentence}
              showSignupPrompt={true}
            />
          )}
        {explanation?.phrase && (
          <div className="flex basis-full flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-cream-dark/60 px-3 py-2">
            <span className="text-sm text-secondary-text">
              {strings.explanation_phrase_label}{" "}
              <span
                lang={r}
                className="font-literata font-semibold text-main-text"
              >
                {explanation.phrase.displayForm}
              </span>
            </span>
            <SaveWordButton
              l={l}
              dictionaryEntryId={explanation.phrase.dictionaryEntryId}
              alreadySaved={explanation.phrase.alreadySaved}
              storySentence={storySentence}
              showSignupPrompt={false}
            />
          </div>
        )}
      </div>
      {/* The dialog is non-modal and focus stays on the word, so the
          explanation (or the error) is announced when it arrives. */}
      <div aria-live="polite">
        {query.isPending && (
          // Placeholder lines hold roughly the explanation's height, so the
          // popup grows little when it arrives.
          <div className="flex flex-col gap-2 py-1">
            <span className="sr-only">{strings.loading_explain}</span>
            <SkeletonBlock className="h-3 w-full" />
            <SkeletonBlock className="h-3 w-11/12" />
            <SkeletonBlock className="h-3 w-3/5" />
          </div>
        )}
        {query.isError && !explanation && (
          <p className="text-sm text-error">{strings.loading_explain_error}</p>
        )}
        {explanation && (
          <p className="select-text leading-relaxed text-main-text">
            <ExplanationText markup={explanation.markup} r={r} />
          </p>
        )}
      </div>
    </div>
  );
}

// ListenSentenceButton plays the TTS audio of the whole sentence the clicked
// word belongs to. The audio bytes are fetched once per popup and cached as
// an object URL; clicking while playing stops playback. Unmounting (closing
// the popup) stops the audio and releases the object URL.
function ListenSentenceButton({
  l,
  storyId,
  r,
  rSentenceIdx,
}: {
  l: string;
  storyId: string;
  r: string;
  rSentenceIdx: number;
}) {
  type PlayState = "idle" | "loading" | "playing" | "error";
  const [playState, setPlayState] = useState<PlayState>("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    // Reset on every (re-)mount: React StrictMode runs mount -> cleanup ->
    // mount in development, and a ref left true by the simulated unmount
    // would make every fetch result look like it arrived after close.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      audioRef.current?.pause();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const handleClick = async () => {
    if (playState === "loading") return;
    if (playState === "playing") {
      audioRef.current?.pause();
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      setPlayState("idle");
      return;
    }
    try {
      if (!objectUrlRef.current) {
        setPlayState("loading");
        const objectUrl = await fetchSentenceAudioUrl(storyId, r, rSentenceIdx);
        if (unmountedRef.current) {
          // The popup closed while the audio was loading; don't start
          // playback from beyond the grave.
          URL.revokeObjectURL(objectUrl);
          return;
        }
        objectUrlRef.current = objectUrl;
        const audio = new Audio(objectUrl);
        audio.onended = () => {
          if (!unmountedRef.current) setPlayState("idle");
        };
        audioRef.current = audio;
      }
      await audioRef.current!.play();
      setPlayState("playing");
    } catch (err) {
      console.error("Failed to play sentence audio", err);
      if (!unmountedRef.current) setPlayState("error");
    }
  };

  const strings = lstr(l);
  const label = (() => {
    switch (playState) {
      case "loading":
        return strings.listen_loading;
      case "playing":
        return strings.listen_stop;
      default:
        return strings.listen_button;
    }
  })();

  return (
    <div className="flex items-center gap-2">
      {/* aria-disabled, not disabled: a disabled button drops keyboard
          focus to the page; handleClick already ignores clicks while
          loading. */}
      <button
        type="button"
        aria-disabled={playState === "loading"}
        onClick={handleClick}
        className={`btn h-8 rounded-full px-3 text-sm ${
          playState === "playing"
            ? "bg-primary text-on-primary"
            : "bg-primary-light text-primary hover:bg-primary/15"
        }`}
      >
        {playState === "playing" ? (
          <PiStopFill size={14} />
        ) : (
          <PiSpeakerHigh
            size={16}
            className={playState === "loading" ? "animate-pulse" : ""}
          />
        )}
        {/* The Listen/Stop labels are stacked in one grid cell with the
            inactive ones invisible, so the button is always as wide as the
            longer of the two and doesn't resize (shifting the save button
            next to it) when playback toggles. */}
        <span className="grid text-center">
          <span
            className="invisible col-start-1 row-start-1 whitespace-nowrap"
            aria-hidden="true"
          >
            {strings.listen_button}
          </span>
          <span
            className="invisible col-start-1 row-start-1 whitespace-nowrap"
            aria-hidden="true"
          >
            {strings.listen_stop}
          </span>
          <span className="col-start-1 row-start-1 whitespace-nowrap">
            {label}
          </span>
        </span>
      </button>
      {playState === "error" && (
        <span className="text-sm text-error">{strings.listen_error}</span>
      )}
    </div>
  );
}

// SaveWordButton toggles the (already-ingested) dictionary sense in the user's
// saved-word list; while the sense is being prepared, Save shows disabled.
// Saving requires an account, so logged-out readers get an invitation to
// sign up instead (once per popup, via showSignupPrompt). Saving returns
// immediately; the localized description is produced in the background
// server-side. Removing drops only the user's reference, leaving the global
// dictionary entry intact.
function SaveWordButton({
  l,
  dictionaryEntryId,
  alreadySaved,
  storySentence,
  showSignupPrompt,
}: {
  l: string;
  // null while the sense is being prepared.
  dictionaryEntryId: number | null;
  alreadySaved: boolean;
  // Where the word is saved from.
  storySentence: StorySentence;
  showSignupPrompt: boolean;
}) {
  const queryClient = useQueryClient();
  const saveMutation = useSaveWordMutation();
  const removeMutation = useRemoveWordMutation();
  // Reactive hook (not the synchronous isLoggedIn()) so the button doesn't
  // render in the wrong state while Firebase is still hydrating auth.
  const loggedIn = useLoggedIn();
  const strings = lstr(l);
  // Set when the user saved the word in this popup, to animate the change.
  const [justSaved, setJustSaved] = useState(false);

  // Saving replaces the save button with the remove button and vice versa.
  // If the activated button had keyboard focus, hand focus to its
  // replacement instead of letting it fall back to the page. The buttons use
  // aria-disabled while pending so they keep focus until then.
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const moveFocusToReplacementRef = useRef(false);
  useEffect(() => {
    if (!moveFocusToReplacementRef.current) return;
    moveFocusToReplacementRef.current = false;
    (alreadySaved ? removeButtonRef : saveButtonRef).current?.focus();
  }, [alreadySaved]);

  if (!loggedIn) {
    return showSignupPrompt ? (
      <Link
        to="/signup"
        className="ml-auto text-sm font-semibold text-primary underline-offset-2 hover:underline"
      >
        {strings.signup_to_save}
      </Link>
    ) : null;
  }

  // alreadySaved comes from the explain query, which both save and remove keep
  // up to date in the query cache, so this stays correct across popup reopens.
  if (alreadySaved && dictionaryEntryId !== null) {
    return (
      <div className="ml-auto flex flex-wrap items-center gap-x-1 gap-y-1">
        <span
          className={`flex items-center gap-1.5 text-sm font-semibold text-primary ${
            justSaved ? "animate-bump" : ""
          }`}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4.5 10.5l3.5 3.5 7.5-8"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={justSaved ? "animate-draw" : ""}
            />
          </svg>
          {strings.word_saved}
        </span>
        <button
          ref={removeButtonRef}
          type="button"
          aria-label={strings.remove_word_button}
          title={strings.remove_word_button}
          aria-disabled={removeMutation.isPending}
          onClick={(e) => {
            if (removeMutation.isPending) return;
            moveFocusToReplacementRef.current =
              document.activeElement === e.currentTarget;
            setJustSaved(false);
            removeMutation.mutate(
              { dictionaryEntryId },
              { onError: () => (moveFocusToReplacementRef.current = false) },
            );
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full text-secondary-text transition-colors hover:bg-cream-dark hover:text-error aria-disabled:opacity-50"
        >
          <PiTrash size={16} />
        </button>
        {removeMutation.isError && (
          <span className="text-sm text-error">
            {strings.remove_word_error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-1">
      <button
        ref={saveButtonRef}
        type="button"
        aria-disabled={dictionaryEntryId === null || saveMutation.isPending}
        onClick={(e) => {
          if (dictionaryEntryId === null || saveMutation.isPending) return;
          moveFocusToReplacementRef.current =
            document.activeElement === e.currentTarget;
          saveMutation.mutate(
            { dictionaryEntryId, l, storySentence },
            {
              onSuccess: () => {
                setJustSaved(true);
                invalidateStoryPracticeCount(
                  queryClient,
                  storySentence.r,
                  storySentence.storyId,
                );
              },
              onError: () => (moveFocusToReplacementRef.current = false),
            },
          );
        }}
        className="btn-primary h-8 rounded-full px-3.5 text-sm"
      >
        <PiPlusBold size={12} />
        {dictionaryEntryId === null
          ? strings.save_word_preparing
          : saveMutation.isPending
            ? strings.saving_word
            : strings.save_word_button}
      </button>
      {saveMutation.isError && (
        <span className="text-sm text-error">
          {saveMutation.error.message === SAVE_WORD_LIMIT_ERROR
            ? strings.save_word_limit_error
            : strings.save_word_error}
        </span>
      )}
    </div>
  );
}
